import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, ArrowLeft, Loader, AlertCircle, Mail, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Professor Create Page component
 * Allows admins and moderators to add a new professor
 */
const ProfessorCreatePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Form state
  const [professorForm, setProfessorForm] = useState({
    name: '',
    email: '',
    bio: ''
  });
  
  // Component state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if user is admin or moderator
  const isAuthorized = user && (user.role === 'admin' || user.role === 'moderator');
  
  // Get school ID - check both potential locations
  const schoolId = user?.schoolId || user?.school?.id;
  
  useEffect(() => {
    // Redirect if user doesn't have permission
    if (!isAuthorized) {
      toast.error('You do not have permission to add professors');
      navigate('/professors');
      return;
    }
    
    // Check for required school ID
    if (!schoolId) {
      setError('No school information available. Please contact an administrator.');
    }
  }, [isAuthorized, navigate, schoolId]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfessorForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!professorForm.name) {
      toast.error('Professor name is required');
      return;
    }
    
    // Check for school ID
    if (!schoolId) {
      toast.error('School information is missing');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Submit form to API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/professors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...professorForm,
          schoolId: schoolId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create professor');
      }
      
      const newProfessor = await response.json();
      
      toast.success('Professor added successfully');
      navigate(`/professors/${newProfessor.id}`);
    } catch (error) {
      console.error('Error creating professor:', error);
      toast.error(error.message || 'Failed to create professor');
      setError(error.message || 'Failed to create professor');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isAuthorized) {
    return null; // Don't render anything if not authorized
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/professors')}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          <span>Back to Professors</span>
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-start">
          <User className="h-8 w-8 text-red-500 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Add New Professor
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Add a professor to {user?.school?.name || 'your school'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-400">{error}</span>
          </div>
        </div>
      )}
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Professor Details
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Professor's full name"
                value={professorForm.name}
                onChange={handleChange}
                className="pl-10 block w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="professor@university.edu"
                value={professorForm.email}
                onChange={handleChange}
                className="pl-10 block w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Optional contact email for the professor
            </p>
          </div>
          
          <div>
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Biography
            </label>
            <div className="relative">
              <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                <FileText className="h-5 w-5 text-gray-400" />
              </div>
              <textarea
                id="bio"
                name="bio"
                rows="4"
                placeholder="Professor's biography, background, areas of expertise, etc."
                value={professorForm.bio}
                onChange={handleChange}
                className="pl-10 block w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Optional biographical information about the professor
            </p>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/professors')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !schoolId}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader className="inline h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                'Add Professor'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfessorCreatePage;